import { Box, Checkbox, FormControlLabel } from '@material-ui/core';
import { useFormik } from 'formik';
import React from 'react';
import {
  ISetupData,
  pullSetupData,
  pushCreateRole,
  pushUser
} from './manage-users';
import addUserIcon from '../common/static/images/add-user.png';
import { State } from '../common-package/type';
import CustomTextField from '../common-package/CustomTextField';
import FunctionDialog, { IStep } from '../common-package/FunctionDialog';
import { requiredMsg } from '../common-package/constant';
import SelectField from '../common-package/SelectField';
import SuccessSnackbar from '../common-package/SuccessSnackbar';
import FailureSnackbar from '../common-package/FailureSnackbar';

interface ICreateUsersProps {
  open: boolean;
  onClose: () => void;
  authenticated?: boolean;
}

interface IFormProps {
  readonly firstName: string;
  readonly lastName: string;
  readonly role: number;
  readonly department: number;
  readonly email: string;
  readonly password: string;
  readonly emailNotification: [];
  readonly usernameIsEmail: boolean;
  readonly username: string;
}
const CreateUsers: React.FunctionComponent<ICreateUsersProps> = ({
  open,
  onClose,
  authenticated
}) => {
  const [{ roles, departments }, setSetupData] = React.useState<ISetupData>({
    roles: [],
    departments: []
  });
  const [submitState, setSubmitState] = React.useState<State>(State.DEFAULT);
  const load = async () => {
    const setup = await pullSetupData();
    setSetupData(setup);
    console.log(setup.roles);
  };
  React.useEffect(() => {
    if (authenticated) {
      void load();
    }
  }, [authenticated]);
  const {
    values,
    setFieldValue,
    setSubmitting,
    isSubmitting,
    errors,
    handleSubmit,
    setFieldError
  } = useFormik<IFormProps>({
    initialValues: {
      firstName: '',
      lastName: '',
      role: 0,
      department: 0,
      email: '',
      password: '',
      emailNotification: [],
      usernameIsEmail: true,
      username: ''
    },
    onSubmit: async (formValues, { resetForm }) => {
      const result = await pushUser(formValues);
      if (result) {
        setSubmitState(State.SUCCESS);
        console.log('Success');
        resetForm({});
        onClose();
        return true;
      } else {
        setSubmitState(State.FAIL);
        return false;
      }
    }
  });

  // Check 'Use email as user name' --> set username = email
  React.useEffect(() => {
    if (values.usernameIsEmail) {
      setFieldValue('username', values.email);
    }
  }, [values.usernameIsEmail, values.email, setFieldValue]);

  const steps: IStep[] = [
    {
      label: 'Personal Information',
      content: (
        <Box>
          <CustomTextField
            label="First Name"
            value={values.firstName}
            required={true}
            onChange={newValue =>
              setFieldValue('firstName', newValue.target.value)
            }
            errorMsg={errors.firstName}
          />
          <CustomTextField
            label="Last Name"
            value={values.lastName}
            required
            errorMsg={errors.lastName}
            onChange={newValue =>
              setFieldValue('lastName', newValue.target.value)
            }
          />
        </Box>
      ),
      validate: () => {
        let isValid = true;
        if (values.firstName === '') {
          setFieldError('firstName', requiredMsg);
          isValid = false;
        }
        if (values.lastName === '') {
          setFieldError('lastName', requiredMsg);
          isValid = false;
        }
        return isValid;
      }
    },
    {
      label: 'Position Details',
      content: (
        <Box>
          <SelectField
            label="Role"
            required
            options={roles}
            error={!!errors.role}
            disabled={isSubmitting}
            value={values.role}
            onChange={async (_, newValue) => {
              if (newValue?.id !== -1) {
                setFieldValue('role', newValue?.id);
              } else {
                setSubmitting(true);
                const result = await pushCreateRole({ name: newValue.label });
                if (result) {
                  await load();
                  setSubmitting(false);
                  setFieldValue('role', result.id);
                } else {
                  setSubmitting(false);
                  setFieldValue('role', 0);
                }
              }
            }}
            onInputChange={value => {
              if (value !== '') {
                setSetupData({
                  departments,
                  roles: [
                    ...roles.filter(role => role.id !== -1),
                    { id: -1, label: value }
                  ]
                });
              } else {
                setSetupData({
                  departments,
                  roles: [...roles.filter(role => role.id !== -1)]
                });
              }
            }}
          />
          <SelectField
            label="Department"
            options={departments}
            required
            value={values.department}
            errorMsg={errors.department}
            onChange={(_, newValue) =>
              setFieldValue('department', newValue?.id)
            }
          />
          <CustomTextField
            label="Work Email"
            value={values.email}
            required
            errorMsg={errors.email}
            onChange={newValue => setFieldValue('email', newValue.target.value)}
            type="email"
          />
        </Box>
      ),
      validate: () => {
        let isValid = true;
        if (values.role === 0) {
          setFieldError('role', requiredMsg);
          isValid = false;
        }
        if (values.department === 0) {
          setFieldError('department', requiredMsg);
          isValid = false;
        }
        if (values.email === '') {
          setFieldError('email', requiredMsg);
          isValid = false;
        }
        return isValid;
      }
    },
    {
      label: 'Login Details',
      content: (
        <Box>
          <Box mb={1}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={values.usernameIsEmail}
                  onChange={() =>
                    setFieldValue('usernameIsEmail', !values.usernameIsEmail)
                  }
                  color="primary"
                />
              }
              label="Set email as username"
            />
          </Box>
          <CustomTextField
            label="Username"
            disabled={values.usernameIsEmail}
            value={values.username}
            required
            errorMsg={errors.username}
            onChange={newValue =>
              setFieldValue('username', newValue.target.value)
            }
          />
          <CustomTextField
            errorMsg={errors.password}
            label="Password"
            value={values.password}
            required
            onChange={newValue =>
              setFieldValue('password', newValue.target.value)
            }
            type="password"
          />
        </Box>
      ),
      validate: () => {
        let isValid = true;
        if (values.username === '') {
          setFieldError('username', requiredMsg);
          isValid = false;
        }

        if (values.password === '') {
          setFieldError('password', requiredMsg);
          isValid = false;
        }
        return isValid;
      }
    }
  ];
  return (
    <>
      <SuccessSnackbar
        state={submitState}
        onClose={() => setSubmitState(State.DEFAULT)}
        message={'New user created'}
      />

      <FailureSnackbar
        state={submitState}
        onClose={() => setSubmitState(State.DEFAULT)}
        message={'Fail to create new user'}
      />
      <FunctionDialog
        steps={steps}
        handleSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        open={open}
        onClose={onClose}
        submitText="Add User"
        title="Add New User"
        iconUri={addUserIcon}
      />
    </>
  );
};

export default CreateUsers;
